// ExternalRequestHistory.tsx
import React, { useEffect, useState } from "react";
import { Box, Typography, Modal, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from "@mui/material";
import ReactDataGrid from '@inovua/reactdatagrid-community';
import '@inovua/reactdatagrid-community/index.css';
import { useNavigate } from 'react-router-dom';
import NavBar from "../NavBar/NavBar";
import FooterData from "../NavBar/FooterData";
import BackButton from "../../../utils/BackButton";
import { getIdMeBaseApiUrl } from "../../../utils/UrlConstants";
import { checkUserAuthentication, handleUnauthenticatedUser } from "../../../utils/authUtils";
import { base64Utils } from "../../../utils/base64Utils";
import CloseIcon from '@mui/icons-material/Close';
import { useIdentityAppService } from "../../../services/IdentityServices";

interface UserAppRequestDto {
    id: number;
    userId: number;
    userLogin: string;
    firstName: string;
    lastName: string;
    userEmail: string;
    applicationCode: string;
    applicationName: string;
    roleCode: string;
    roleName: string;
    assignedTo: string;
    status: string;
    operationalFlag: string;
    createdDate: string;
    modifiedDate: string;
    createdBy: string;
    modifiedBy: string;
    notes: string;
    environment?: string;
    environmentCode?: string;
    requestedDate?: string;
    approvedBy?: string;
}

const ExternalRequestHistory: React.FC = () => {
    const navigate = useNavigate();
    const [userRequests, setUserRequests] = useState<UserAppRequestDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isAuthenticating, setIsAuthenticating] = useState(true);
    
    // Modal state for request details
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<UserAppRequestDto | null>(null);
    const [requestLifecycle, setRequestLifecycle] = useState<UserAppRequestDto[]>([]);
    const [loadingLifecycle, setLoadingLifecycle] = useState(false);
    const [lifecycleError, setLifecycleError] = useState<string | null>(null);

    // Helper function to get current user ID from sessionStorage
    const getCurrentUserIdFromSession = () => {
        try {
            const storedUserData = sessionStorage.getItem('currentUserData');
            const userData = storedUserData ? JSON.parse(storedUserData) : null;
            return userData?.userId || null;
        } catch (error) {
            console.error("Error parsing current user data from session:", error);
            return null;
        }
    };

    // Handle back button click - navigate to external home
    const handleBackButtonClick = (event: React.MouseEvent) => {
        event.preventDefault();
        navigate('/external-home');
    };

    useEffect(() => {
        if (!checkUserAuthentication()) {
            handleUnauthenticatedUser();
            return;
        }

        setIsAuthenticating(false);
        fetchRoleRequests();
    }, []);

    const fetchRoleRequests = async () => {
        const token = sessionStorage.getItem("external_token");
        const userId = getCurrentUserIdFromSession();
        
        if (token) {
            const parsedToken = JSON.parse(token);
            const username = parsedToken.username;

            if (username || userId) {
                try {
                    setLoading(true);
                    setError(null);

                    let response;
                    response = await useIdentityAppService().getUserRequests(userId);

                    // Handle different response formats
                    const requests = response.data.requests || response.data || [];
                    setUserRequests(requests);
                } catch (error) {
                    console.error("Error fetching role requests:", error);
                    setError("Failed to load role requests");
                    setUserRequests([]);
                } finally {
                    setLoading(false);
                }
            }
        } else {
            setError("User token not found");
            setLoading(false);
        }
    };

    // Function to fetch request lifecycle
    const fetchRequestLifecycle = async (requestId: number) => {
        setLoadingLifecycle(true);
        setLifecycleError(null);
        
        try {
            const response = await useIdentityAppService().getRequestLifecycle(requestId);
            setRequestLifecycle(response.data || []);
        } catch (error) {
            console.error('Error fetching request lifecycle:', error);
            setLifecycleError('Failed to load request history');
            setRequestLifecycle([]);
        } finally {
            setLoadingLifecycle(false);
        }
    };

    // Handle opening details modal
    const handleOpenDetails = (request: UserAppRequestDto) => {
        setSelectedRequest(request);
        setDetailsModalOpen(true);
        fetchRequestLifecycle(request.id);
    };

    // Handle closing details modal
    const handleCloseDetails = () => {
        setDetailsModalOpen(false);
        setSelectedRequest(null);
        setRequestLifecycle([]);
        setLifecycleError(null);
    };

    // Function to get status styles
    const getStatusStyles = (status: string) => {
        switch (status?.toUpperCase()) {
            case 'APPROVED':
            case 'ACTIVE':
                return {
                    backgroundColor: '#E8F5E8',
                    color: '#4CAF50'
                };
            case 'PENDING':
            case 'SUBMITTED':
                return {
                    backgroundColor: '#fff3cd',
                    color: '#856404'
                };
            case 'REJECTED':
            case 'DENIED':
                return {
                    backgroundColor: '#FFEBEE',
                    color: '#f44336'
                };
            case 'IN_REVIEW':
            case 'REVIEWING':
                return {
                    backgroundColor: '#E3F2FD',
                    color: '#1976D2'
                };
            case 'CANCELLED':
            case 'CANCELED':
                return {
                    backgroundColor: '#F5F5F5',
                    color: '#9E9E9E'
                };
            case 'EXPIRED':
                return {
                    backgroundColor: '#F3E5F5',
                    color: '#7B1FA2'
                };
            default:
                return {
                    backgroundColor: '#f5f5f5',
                    color: '#666'
                };
        }
    };

    // Grid columns for role requests
    const requestGridColumns = [
        {
            name: 'id',
            header: 'Request ID',
            defaultWidth: 100,
            type: 'number',
        },
        {
            name: 'applicationName',
            header: 'Application',
            defaultFlex: 1,
            minWidth: 150,
            render: ({ value }: { value: string }) => value || 'N/A'
        },
        {
            name: 'environment',
            header: 'Environment',
            defaultWidth: 120,
            render: ({ value }: { value: string }) => value || 'N/A'
        },
        {
            name: 'roleName',
            header: 'Requested Role',
            defaultFlex: 1,
            minWidth: 150,
            render: ({ value }: { value: string }) => value || 'N/A'
        },
        {
            name: 'status',
            header: 'Status',
            defaultWidth: 120,
            render: ({ value }: { value: string }) => {
                const statusStyles = getStatusStyles(value);

                return (
                    <span style={{
                        padding: '4px 10px',
                        borderRadius: '4px',
                        backgroundColor: statusStyles.backgroundColor,
                        color: statusStyles.color,
                        fontSize: '12px',
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                    }}>
                        {value || 'Unknown'}
                    </span>
                );
            }
        },
        {
            name: 'createdDate',
            header: 'Requested Date',
            defaultWidth: 130,
            render: ({ value }: { value: string }) => {
                return value ? new Date(value).toLocaleDateString() : '';
            }
        },
        {
            name: 'modifiedDate',
            header: 'Last Modified',
            defaultWidth: 130,
            render: ({ value }: { value: string }) => {
                return value ? new Date(value).toLocaleDateString() : '';
            }
        },
        {
            name: 'notes',
            header: 'Notes',
            defaultWidth: 200,
            render: ({ value }: { value: string }) => {
                if (!value) return 'N/A';
                
                // Decode base64 notes
                const decodedNotes = base64Utils.decode(value);
                
                return (
                    <div 
                        style={{ 
                            maxWidth: '180px', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                        title={decodedNotes}
                        dangerouslySetInnerHTML={{ __html: decodedNotes }}
                    />
                );
            }
        },
        {
            name: 'actions',
            header: 'Actions',
            defaultWidth: 100,
            render: ({ data }: { data: UserAppRequestDto }) => (
                <button
                    style={{
                        backgroundColor: '#00607f',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 'bold'
                    }}
                    onClick={() => handleOpenDetails(data)}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#004d66'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#00607f'}
                >
                    Details
                </button>
            )
        }
    ];

    // Show loading state while authenticating
    if (isAuthenticating) {
        return (
            <>
                <NavBar />
                <Box sx={{
                    padding: 4,
                    minHeight: 'calc(100vh - 64px)',
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <Typography variant="h5" sx={{ color: '#333' }}>
                        Authenticating...
                    </Typography>
                </Box>
                <FooterData styles={true} />
            </>
        );
    }

    if (loading) {
        return (
            <>
                <NavBar />
                <Box sx={{
                    padding: 4,
                    minHeight: 'calc(100vh - 64px)',
                    backgroundColor: '#f5f5f5'
                }}>
                    {/* Back Button */}
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'flex-start',
                        marginBottom: 2,
                        marginLeft: -2,
                        marginTop: -2
                    }}>
                        <BackButton onClick={handleBackButtonClick} />
                    </Box>

                    <Typography
                        variant="h4"
                        sx={{
                            textAlign: 'center',
                            marginBottom: 4,
                            fontWeight: 'bold',
                            color: '#333'
                        }}
                    >
                        External Request History
                    </Typography>
                    <Box sx={{ textAlign: 'center', padding: 4 }}>
                        <Typography variant="h6">
                            Loading request history...
                        </Typography>
                    </Box>
                </Box>
                <FooterData styles={true} />
            </>
        );
    }

    if (error) {
        return (
            <>
                <NavBar />
                <Box sx={{
                    padding: 4,
                    minHeight: 'calc(100vh - 64px)',
                    backgroundColor: '#f5f5f5'
                }}>
                    {/* Back Button */}
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'flex-start',
                        marginBottom: 2,
                        marginLeft: -2,
                        marginTop: -2
                    }}>
                        <BackButton onClick={handleBackButtonClick} />
                    </Box>

                    <Typography
                        variant="h4"
                        sx={{
                            textAlign: 'center',
                            marginBottom: 4,
                            fontWeight: 'bold',
                            color: '#333'
                        }}
                    >
                        External Request History
                    </Typography>
                    <Box sx={{
                        textAlign: 'center',
                        padding: 4,
                        maxWidth: 600,
                        margin: '0 auto'
                    }}>
                        <Typography variant="h6" sx={{ color: '#f44336', marginBottom: 2 }}>
                            Error Loading Request History
                        </Typography>
                        <Typography sx={{ color: '#666' }}>
                            {error}
                        </Typography>
                    </Box>
                </Box>
                <FooterData styles={true} />
            </>
        );
    }

    return (
        <>
            <NavBar />
            <Box sx={{
                padding: 4,
                minHeight: 'calc(100vh - 64px)',
                backgroundColor: '#f5f5f5'
            }}>
                {/* Back Button */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-start',
                    marginBottom: 2,
                    marginLeft: -2,
                    marginTop: -2
                }}>
                    <BackButton onClick={handleBackButtonClick} />
                </Box>

                <Typography
                    variant="h4"
                    sx={{
                        textAlign: 'center',
                        marginBottom: 4,
                        fontWeight: 'bold',
                        color: '#333'
                    }}
                >
                    External Request History
                </Typography>

                <Box sx={{
                    backgroundColor: 'white',
                    borderRadius: 2,
                    padding: 3,
                    boxShadow: 1
                }}>
                    <Typography
                        variant="h6"
                        sx={{
                            marginBottom: 3,
                            color: '#00607f',
                            borderBottom: '2px solid #00607f',
                            paddingBottom: 1
                        }}
                    >
                        Role Requests ({userRequests.length})
                    </Typography>

                    {userRequests.length > 0 ? (
                        <ReactDataGrid
                            licenseKey="AppName=CoherenceApp,Company=Coherence,ExpiryDate=2025-04-15T00:00:00.000Z,LicenseDeveloperCount=1,LicenseType=single_app,Ref=CoherenceLicenseRef,Z=983320388-1513273079-1127323721-2081795733-199375339958733905"
                            idProperty="id"
                            pagination
                            columns={requestGridColumns}
                            dataSource={userRequests}
                            sortable={true}
                            defaultLimit={10}
                            rowHeight={50}
                            enableColumnAutosize={true}
                            style={{ minHeight: 400 }}
                            defaultSortInfo={[{ name: 'createdDate', dir: -1 }]}
                        />
                    ) : (
                        <Box sx={{
                            textAlign: 'center',
                            padding: 6,
                            backgroundColor: '#f8f9fa',
                            borderRadius: 2
                        }}>
                            <Typography variant="h6" sx={{ marginBottom: 2, color: '#666' }}>
                                No Request History
                            </Typography>
                            <Typography sx={{ color: '#666' }}>
                                You have not submitted any role requests yet.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Request Details Modal */}
            <Dialog
                open={detailsModalOpen}
                onClose={handleCloseDetails}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    style: {
                        minHeight: '80vh',
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    bgcolor: '#00607f', 
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography variant="h6">
                        Request Details - ID: {selectedRequest?.id}
                    </Typography>
                    <IconButton
                        onClick={handleCloseDetails}
                        sx={{ color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                
                <DialogContent sx={{ padding: 3 }}>
                    {selectedRequest && (
                        <>
                            {/* Request Information Grid */}
                            <Box sx={{ marginBottom: 4 }}>
                                <Box sx={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: '1fr 1fr', 
                                    gap: 3, 
                                    marginBottom: 3 
                                }}>
                                    <Box>
                                        <Typography variant="h6" sx={{ 
                                            marginBottom: 2,
                                            color: '#00607f',
                                            borderBottom: '2px solid #00607f',
                                            paddingBottom: 1
                                        }}>
                                            User Information
                                        </Typography>
                                        <Box sx={{ 
                                            backgroundColor: '#f8f9fa', 
                                            padding: 2, 
                                            borderRadius: 1 
                                        }}>
                                            <Typography variant="body2" sx={{ marginBottom: 1 }}>
                                                <strong>User ID:</strong> {selectedRequest.userId}
                                            </Typography>
                                            <Typography variant="body2" sx={{ marginBottom: 1 }}>
                                                <strong>Username:</strong> {selectedRequest.userLogin}
                                            </Typography>
                                            <Typography variant="body2" sx={{ marginBottom: 1 }}>
                                                <strong>Name:</strong> {selectedRequest.firstName} {selectedRequest.lastName}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Email:</strong> {selectedRequest.userEmail}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    
                                    <Box>
                                        <Typography variant="h6" sx={{ 
                                            marginBottom: 2,
                                            color: '#00607f',
                                            borderBottom: '2px solid #00607f',
                                            paddingBottom: 1
                                        }}>
                                            Request Information
                                        </Typography>
                                        <Box sx={{ 
                                            backgroundColor: '#f8f9fa', 
                                            padding: 2, 
                                            borderRadius: 1 
                                        }}>
                                            <Typography variant="body2" sx={{ marginBottom: 1 }}>
                                                <strong>Request ID:</strong> {selectedRequest.id}
                                            </Typography>
                                            <Typography variant="body2" sx={{ marginBottom: 1 }}>
                                                <strong>Status:</strong>
                                                <Box component="span" sx={{
                                                    padding: '4px 10px',
                                                    borderRadius: '4px',
                                                    backgroundColor: getStatusStyles(selectedRequest.status).backgroundColor,
                                                    color: getStatusStyles(selectedRequest.status).color,
                                                    marginLeft: 1,
                                                    fontSize: '12px',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {selectedRequest.status}
                                                </Box>
                                            </Typography>
                                            <Typography variant="body2" sx={{ marginBottom: 1 }}>
                                                <strong>Assigned To:</strong> {selectedRequest.assignedTo}
                                            </Typography>
                                            <Typography variant="body2" sx={{ marginBottom: 1 }}>
                                                <strong>Created:</strong> {new Date(selectedRequest.createdDate).toLocaleString()}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Modified:</strong> {new Date(selectedRequest.modifiedDate).toLocaleString()}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                <Box sx={{ marginBottom: 3 }}>
                                    <Typography variant="h6" sx={{ 
                                        marginBottom: 2,
                                        color: '#00607f',
                                        borderBottom: '2px solid #00607f',
                                        paddingBottom: 1
                                    }}>
                                        Role & Application Details
                                    </Typography>
                                    <Box sx={{ 
                                        backgroundColor: '#f8f9fa', 
                                        padding: 2, 
                                        borderRadius: 1 
                                    }}>
                                        <Typography variant="body2" sx={{ marginBottom: 1 }}>
                                            <strong>Application:</strong> {selectedRequest.applicationName} ({selectedRequest.applicationCode})
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Role:</strong> {selectedRequest.roleName} ({selectedRequest.roleCode})
                                        </Typography>
                                    </Box>
                                </Box>

                                {selectedRequest.notes && (
                                    <Box sx={{ marginBottom: 3 }}>
                                        <Typography variant="h6" sx={{ 
                                            marginBottom: 2,
                                            color: '#00607f',
                                            borderBottom: '2px solid #00607f',
                                            paddingBottom: 1
                                        }}>
                                            Notes
                                        </Typography>
                                        <Box sx={{
                                            padding: 2,
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: 1,
                                            border: '1px solid #dee2e6'
                                        }}>
                                            <div dangerouslySetInnerHTML={{
                                                __html: base64Utils.decode(selectedRequest.notes)
                                            }} />
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            {/* Request Lifecycle Timeline */}
                            <Box>
                                <Typography variant="h6" sx={{ 
                                    marginBottom: 2,
                                    color: '#00607f',
                                    borderBottom: '2px solid #00607f',
                                    paddingBottom: 1
                                }}>
                                    Request History & Lifecycle
                                </Typography>
                                
                                {loadingLifecycle ? (
                                    <Box sx={{ textAlign: 'center', padding: 3, color: '#666' }}>
                                        <Typography>Loading request history...</Typography>
                                    </Box>
                                ) : lifecycleError ? (
                                    <Box sx={{
                                        textAlign: 'center',
                                        padding: 3
                                    }}>
                                        <Typography sx={{ color: '#f44336' }}>
                                            {lifecycleError}
                                        </Typography>
                                    </Box>
                                ) : requestLifecycle.length > 0 ? (
                                    <Box sx={{
                                        backgroundColor: '#f8f9fa',
                                        padding: 3,
                                        borderRadius: 1,
                                        border: '1px solid #dee2e6'
                                    }}>
                                        {requestLifecycle.map((lifecycleItem, index) => {
                                            const isLast = index === requestLifecycle.length - 1;
                                            const itemStatusStyles = getStatusStyles(lifecycleItem.status);
                                            
                                            return (
                                                <Box key={lifecycleItem.id} sx={{ 
                                                    position: 'relative',
                                                    paddingLeft: '40px',
                                                    paddingBottom: isLast ? 0 : 3
                                                }}>
                                                    {/* Timeline line */}
                                                    {!isLast && (
                                                        <Box sx={{
                                                            position: 'absolute',
                                                            left: '15px',
                                                            top: '25px',
                                                            bottom: '0',
                                                            width: '2px',
                                                            backgroundColor: '#dee2e6'
                                                        }} />
                                                    )}
                                                    
                                                    {/* Timeline dot */}
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        left: '8px',
                                                        top: '8px',
                                                        width: '16px',
                                                        height: '16px',
                                                        borderRadius: '50%',
                                                        backgroundColor: itemStatusStyles.color,
                                                        border: '3px solid white',
                                                        boxShadow: `0 0 0 2px ${itemStatusStyles.color}`
                                                    }} />
                                                    
                                                    {/* Timeline content */}
                                                    <Box sx={{
                                                        backgroundColor: 'white',
                                                        padding: 2,
                                                        borderRadius: 1,
                                                        border: '1px solid #dee2e6',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                    }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'flex-start',
                                                            marginBottom: 1
                                                        }}>
                                                            <Box>
                                                                <Box component="span" sx={{
                                                                    padding: '3px 8px',
                                                                    borderRadius: '4px',
                                                                    backgroundColor: itemStatusStyles.backgroundColor,
                                                                    color: itemStatusStyles.color,
                                                                    fontSize: '12px',
                                                                    fontWeight: 'bold',
                                                                    marginRight: 1
                                                                }}>
                                                                    {lifecycleItem.status}
                                                                </Box>
                                                                <Typography variant="caption" sx={{
                                                                    color: '#666',
                                                                    fontWeight: 'bold'
                                                                }}>
                                                                    Request ID: {lifecycleItem.id}
                                                                </Typography>
                                                            </Box>
                                                            <Box component="span" sx={{
                                                                fontSize: '12px',
                                                                color: '#666',
                                                                backgroundColor: lifecycleItem.operationalFlag === 'ACTIVE' ? '#e8f5e8' : '#f8f9fa',
                                                                padding: '2px 6px',
                                                                borderRadius: '3px',
                                                                border: `1px solid ${lifecycleItem.operationalFlag === 'ACTIVE' ? '#4CAF50' : '#dee2e6'}`
                                                            }}>
                                                                {lifecycleItem.operationalFlag}
                                                            </Box>
                                                        </Box>
                                                        
                                                        <Box sx={{
                                                            display: 'grid',
                                                            gridTemplateColumns: '1fr 1fr',
                                                            gap: 2,
                                                            fontSize: '14px'
                                                        }}>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
                                                                    <strong>Created:</strong> {new Date(lifecycleItem.createdDate).toLocaleString()}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
                                                                    <strong>Created By:</strong> {lifecycleItem.createdBy || 'N/A'}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
                                                                    <strong>Modified:</strong> {new Date(lifecycleItem.modifiedDate).toLocaleString()}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    <strong>Modified By:</strong> {lifecycleItem.modifiedBy || 'N/A'}
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="body2">
                                                                    <strong>Assigned To:</strong> {lifecycleItem.assignedTo}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        
                                                        {lifecycleItem.notes && (
                                                            <Box sx={{
                                                                marginTop: 1,
                                                                padding: 1,
                                                                backgroundColor: '#f8f9fa',
                                                                borderRadius: 1,
                                                                fontSize: '13px'
                                                            }}>
                                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                    Notes:
                                                                </Typography>
                                                                <Box sx={{ marginTop: 0.5 }}>
                                                                    {lifecycleItem.notes}
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                ) : (
                                    <Box sx={{
                                        textAlign: 'center',
                                        padding: 3,
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: 1,
                                        border: '1px solid #dee2e6'
                                    }}>
                                        <Typography sx={{ color: '#666' }}>
                                            No request history available
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}
                </DialogContent>
                
                <DialogActions sx={{ padding: 2, bgcolor: '#f8f9fa' }}>
                    <Button 
                        onClick={handleCloseDetails} 
                        variant="contained"
                        sx={{ bgcolor: '#00607f' }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <FooterData styles={true} />
        </>
    );
};

export default ExternalRequestHistory;